import { useEffect, useState } from "react";
import { collection, doc, getDoc, serverTimestamp, setDoc, writeBatch } from "firebase/firestore";
import {
  Battery,
  Boxes,
  Droplets,
  Gauge,
  Package,
  Search,
  SunMedium,
  Waves,
  Wrench,
  Zap,
} from "lucide-react";
import { db } from "./siteData";

/* -------------------------------------------------------------------------- */
/*  ICONS                                                                     */
/*  Icons are stored as short keys. The shop page and the manager both map    */
/*  them through ICONS, so they can never drift apart.                        */
/* -------------------------------------------------------------------------- */

export const ICON_OPTIONS = [
  { value: "gauge", label: "Gauge (pumps)", icon: Gauge },
  { value: "sun", label: "Sun (solar)", icon: SunMedium },
  { value: "battery", label: "Battery (power storage)", icon: Battery },
  { value: "waves", label: "Waves (tanks)", icon: Waves },
  { value: "package", label: "Box (general)", icon: Package },
  { value: "droplets", label: "Droplets (water)", icon: Droplets },
  { value: "wrench", label: "Wrench (service)", icon: Wrench },
  { value: "zap", label: "Lightning (power)", icon: Zap },
  { value: "boxes", label: "Boxes (bundles)", icon: Boxes },
  { value: "search", label: "Magnifier (survey)", icon: Search },
];
export const ICONS = Object.fromEntries(ICON_OPTIONS.map((o) => [o.value, o.icon]));

// Keep these in step with the limits in firestore.rules (site/shop).
export const SHOP_LIMITS = {
  categories: 12,
  products: 150,
  services: 12,
  packages: 12,
  packageItems: 10,
  points: 5,
  tags: 4,
};

export const newId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/* -------------------------------------------------------------------------- */
/*  PRICING                                                                   */
/*  A package line can carry its own `price` (each). null means "use the      */
/*  product's catalogue price". The shop and the manager share these.         */
/* -------------------------------------------------------------------------- */

export const lineUnit = (line, product) =>
  line.price !== null && line.price !== undefined ? line.price : product?.price || 0;

export const packageTotal = (pkg, productById) =>
  pkg.contents.reduce((n, c) => n + lineUnit(c, productById[c.id]) * c.qty, 0);

/* -------------------------------------------------------------------------- */
/*  DEFAULTS  (what the shop shows until something is published)              */
/* -------------------------------------------------------------------------- */

const P = (id, cat, svc, name, spec, price, desc, tags) => ({
  id, cat, svc, name, spec, price, desc, tags, img: "", inStock: true,
});

export const DEFAULT_SHOP = {
  // digits only, with country code
  whatsapp: "254700000000",
  categories: [
    { id: "pumps", label: "Water pumps", icon: "gauge" },
    { id: "solar", label: "Solar panels", icon: "sun" },
    { id: "power", label: "Inverters and batteries", icon: "battery" },
    { id: "tanks", label: "Water tanks", icon: "waves" },
    { id: "fittings", label: "Fittings and accessories", icon: "package" },
  ],
  products: [
    P("p-sub-1hp", "pumps", "pump", "Submersible pump 1 HP", "Boreholes up to 60 m deep", 38500, "A dependable pump for household and small farm boreholes.", ["Borehole", "Home and farm"]),
    P("p-sub-2hp", "pumps", "pump", "Submersible pump 2 HP", "Boreholes up to 120 m deep", 62000, "More lift and flow for deeper boreholes, irrigation and livestock.", ["Borehole", "Irrigation"]),
    P("p-surface", "pumps", "pump", "Surface booster pump", "Pressure for taps and tanks", 14800, "Moves water from a tank and keeps pressure steady across the house.", ["Homes", "Pressure"]),
    P("p-solar-pump", "pumps", "solarpump", "Solar pump kit with controller", "Runs straight from panels", 89000, "Pump and controller matched together. Panels are sold separately.", ["Solar", "Off-grid"]),
    P("s-200", "solar", "solar", "Solar panel 200 W", "Monocrystalline", 14500, "A compact panel for small systems, lighting and backup power.", ["Small systems"]),
    P("s-450", "solar", "solar", "Solar panel 450 W", "Monocrystalline, high output", 28500, "High output for homes, farms and solar pumping arrays.", ["Homes", "Pumping"]),
    P("s-mount", "solar", "solar", "Panel mounting rails (set)", "Aluminium, for 2 panels", 6800, "Corrosion-resistant rails and clamps for roof or ground mounting.", ["Mounting"]),
    P("b-inv-3k", "power", "solar", "Hybrid inverter 3 kW", "Pure sine wave", 74000, "Runs household loads from panels, batteries or the grid.", ["Homes", "Backup"]),
    P("b-lith", "power", "solar", "Lithium battery 5 kWh", "Wall mounted", 168000, "Long-life storage for after sunset and during outages.", ["Storage"]),
    P("t-1000", "tanks", "", "Water tank 1,000 L", "UV-stabilised plastic", 11500, "A tough above-ground tank for household storage.", ["Homes"]),
    P("t-5000", "tanks", "", "Water tank 5,000 L", "UV-stabilised plastic", 52000, "Large storage for farms, institutions and businesses.", ["Farms", "Business"]),
    P("f-pipe", "fittings", "pump", "HDPE pipe 1 inch (50 m roll)", "Borehole to tank lines", 9800, "Durable pressure pipe for runs from the borehole to tanks and taps.", ["Piping"]),
    P("f-control", "fittings", "pump", "Pump control box", "Overload and dry-run protection", 7200, "Protects the pump and gives simple on and off control.", ["Protection"]),
    P("f-float", "fittings", "pump", "Float switch", "Automatic tank level control", 1900, "Stops the pump when the tank is full and restarts it when it drops.", ["Automation"]),
  ],
  services: [
    { id: "survey", label: "Water survey and siting", icon: "search", summary: "Know where to drill before any money goes into the ground.", points: ["Site visit and assessment", "A recommended drilling point", "Honest advice on difficult sites"] },
    { id: "borehole", label: "Borehole drilling", icon: "droplets", summary: "Your own dependable water source, drilled and developed until it flows.", points: ["Test hole and drilling", "Casing to keep it open", "Development until it flows"] },
    { id: "pump", label: "Pump installation", icon: "gauge", summary: "The right pump for your borehole depth, yield and daily demand.", points: ["Pump sizing and selection", "Installation and connections", "Testing before we leave"] },
    { id: "solar", label: "Solar installation", icon: "sun", summary: "Panels, inverters and batteries sized for how you actually use power.", points: ["Assessment of your power use", "Clean mounting and wiring", "Testing and handover"] },
    { id: "solarpump", label: "Solar water pumping", icon: "zap", summary: "Pump water with the sun and cut fuel and grid costs.", points: ["Array sized to your pump", "Pump and controller matched", "Installation and testing"] },
    { id: "maintenance", label: "Maintenance and repairs", icon: "wrench", summary: "Keep your water and power running, season after season.", points: ["System checks", "Pump servicing and replacement", "Fault finding and repair"] },
  ],
  packages: [
    { id: "pk-borehole", title: "Borehole pump package", blurb: "Everything to get water from a drilled borehole to your tank.", service: "pump", contents: [{ id: "p-sub-1hp", qty: 1 }, { id: "f-control", qty: 1 }, { id: "f-float", qty: 1 }, { id: "f-pipe", qty: 2 }] },
    { id: "pk-solar-home", title: "Home solar starter", blurb: "Panels, inverter and battery for a home that keeps its lights on.", service: "solar", contents: [{ id: "s-450", qty: 4 }, { id: "s-mount", qty: 2 }, { id: "b-inv-3k", qty: 1 }, { id: "b-lith", qty: 1 }] },
    { id: "pk-solar-pump", title: "Solar water pumping kit", blurb: "Pump water with the sun for irrigation, livestock or the household.", service: "solarpump", contents: [{ id: "p-solar-pump", qty: 1 }, { id: "s-450", qty: 4 }, { id: "s-mount", qty: 2 }, { id: "f-pipe", qty: 1 }, { id: "f-float", qty: 1 }] },
    { id: "pk-storage", title: "Tank and pressure set", blurb: "Large storage with a booster pump and automatic level control.", service: "pump", contents: [{ id: "t-5000", qty: 1 }, { id: "p-surface", qty: 1 }, { id: "f-float", qty: 1 }, { id: "f-pipe", qty: 1 }] },
  ],
};

/* -------------------------------------------------------------------------- */
/*  NORMALISE  (never trust what comes back from the database)                */
/* -------------------------------------------------------------------------- */

const str = (v) => (typeof v === "string" ? v : "");
const list = (v, fallback) => (Array.isArray(v) ? v : fallback);
const money = (v) => (v === null || v === undefined || v === "" ? null : Math.max(0, Math.round(Number(v)) || 0));

export function normalizeShop(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    whatsapp: str(r.whatsapp).replace(/\D/g, "") || DEFAULT_SHOP.whatsapp,
    categories: list(r.categories, DEFAULT_SHOP.categories).map((c) => ({
      id: str(c.id) || newId("c"),
      label: str(c.label),
      icon: ICONS[c.icon] ? c.icon : "package",
    })),
    products: list(r.products, DEFAULT_SHOP.products).map((p) => ({
      id: str(p.id) || newId("p"),
      cat: str(p.cat),
      svc: str(p.svc),
      name: str(p.name),
      spec: str(p.spec),
      price: Math.max(0, Math.round(Number(p.price) || 0)),
      desc: str(p.desc),
      tags: list(p.tags, []).map(str),
      img: str(p.img),
      inStock: p.inStock !== false,
    })),
    services: list(r.services, DEFAULT_SHOP.services).map((s) => ({
      id: str(s.id) || newId("s"),
      label: str(s.label),
      icon: ICONS[s.icon] ? s.icon : "wrench",
      summary: str(s.summary),
      points: list(s.points, []).map(str),
      img: str(s.img),
    })),
    packages: list(r.packages, DEFAULT_SHOP.packages).map((k) => ({
      id: str(k.id) || newId("pk"),
      title: str(k.title),
      blurb: str(k.blurb),
      service: str(k.service),
      img: str(k.img),
      contents: list(k.contents, []).map((c) => ({
        id: str(c.id),
        qty: Math.max(1, Math.min(99, Math.round(Number(c.qty)) || 1)),
        price: money(c.price),
      })),
    })),
  };
}

/* -------------------------------------------------------------------------- */
/*  LOAD / SAVE                                                               */
/* -------------------------------------------------------------------------- */

const ref = () => doc(db, "site", "shop");

export async function loadShop() {
  const snap = await getDoc(ref());
  if (!snap.exists()) {
    return { data: normalizeShop(DEFAULT_SHOP), exists: false, updatedAt: null };
  }
  const d = snap.data();
  return {
    data: normalizeShop(d),
    exists: true,
    updatedAt: d.updatedAt?.toDate?.() || null,
  };
}

export async function saveShop(data, email) {
  await setDoc(ref(), {
    whatsapp: data.whatsapp,
    categories: data.categories,
    products: data.products,
    services: data.services,
    packages: data.packages,
    updatedAt: serverTimestamp(),
    updatedBy: email,
  });
}

/* -------------------------------------------------------------------------- */
/*  ORDERS                                                                    */
/*  One checkout can hold products, services and packages. Each kind is saved */
/*  as its own document so the team can work through them separately:         */
/*    orders/products/quotations/{id}                                         */
/*    orders/services/quotations/{id}                                         */
/*    orders/packages/quotations/{id}                                         */
/*  They share the same `ref` and customer details. A batch writes them all   */
/*  or none, so a half-saved order cannot happen.                             */
/* -------------------------------------------------------------------------- */

const cut = (v, n) => String(v ?? "").trim().slice(0, n);

export async function submitShopOrder(o) {
  const d = o.details;
  const base = {
    ref: cut(o.ref, 30),
    name: cut(d.name, 100),
    phone: cut(d.phone, 40),
    email: cut(d.email, 120),
    county: cut(d.county, 60),
    area: cut(d.area, 100),
    address: cut(d.address, 300),
    notes: cut(d.notes, 1000),
    contactVia: d.contactVia === "email" ? "email" : "whatsapp",
    status: "new",
    createdAt: serverTimestamp(),
  };

  const batch = writeBatch(db);
  const put = (kind, extra) =>
    batch.set(doc(collection(db, "orders", kind, "quotations")), { ...base, ...extra });

  if (o.equipment.length) {
    put("products", {
      items: o.equipment.map((l) => ({ id: cut(l.id, 80), name: cut(l.name, 100), qty: l.qty, price: l.price, total: l.total })),
      subtotal: o.equipmentTotal,
    });
  }
  if (o.services.length) {
    put("services", {
      services: o.services.map((s) => ({ id: cut(s.id, 80), label: cut(s.label, 100) })),
    });
  }
  if (o.packages.length) {
    put("packages", {
      packages: o.packages.map((k) => ({
        id: cut(k.id, 80),
        title: cut(k.title, 100),
        qty: k.qty,
        install: !!k.install,
        installService: cut(k.installService, 100),
        unit: k.unit,
        total: k.total,
        contents: k.contents.map((c) => ({ id: cut(c.id, 80), name: cut(c.name, 100), qty: c.qty, price: c.price })),
      })),
      subtotal: o.packagesTotal,
    });
  }
  await batch.commit();
}

/* -------------------------------------------------------------------------- */
/*  HOOK FOR THE PUBLIC SHOP PAGE                                             */
/*  `ready` flips to true once the database answered (or failed), so visitors */
/*  never see the default catalogue flash before the real one.                */
/* -------------------------------------------------------------------------- */

export function useShopData() {
  const [state, setState] = useState({ shop: normalizeShop(DEFAULT_SHOP), ready: false });

  useEffect(() => {
    let alive = true;
    loadShop()
      .then(({ data }) => alive && setState({ shop: data, ready: true }))
      .catch(() => alive && setState({ shop: normalizeShop(DEFAULT_SHOP), ready: true }));
    return () => {
      alive = false;
    };
  }, []);

  return state;
}